// =============================================================================
// features/configuracoes/canais/PairingModal.tsx — Modal de pareamento do
// aparelho do vendedor (F39-S09).
//
// Fluxo: abre → busca o QR → o vendedor escaneia no celular → o status vira
// WORKING sozinho (polling de 3s) → o modal mostra o desfecho.
//
// LGPD: o QR é CREDENCIAL DE PAREAMENTO. Fica só no estado deste componente,
// não vai para cache (gcTime 0 nos hooks), não é logado e some ao fechar.
//
// DS doc 18: elev-3 no card, overlay com blur, foco visível, Esc fecha.
// =============================================================================

import * as React from 'react';

import { cn } from '../../../lib/cn';

import {
  PAIRING_STATUS_LABEL,
  usePairingQr,
  usePairingStatus,
  type PairingStatus,
} from './useVendedorPairing';

interface PairingModalProps {
  readonly open: boolean;
  readonly channelId: string | null;
  readonly agentName: string;
  readonly onClose: () => void;
}

/** Cor do selo por status — verde só quando de fato conectado. */
function statusTone(status: PairingStatus): string {
  if (status === 'WORKING') return 'bg-success-soft text-success border-success/30';
  if (status === 'FAILED') return 'bg-danger-soft text-danger border-danger/30';
  return 'bg-surface-2 text-ink-3 border-border';
}

export function PairingModal({
  open,
  channelId,
  agentName,
  onClose,
}: PairingModalProps): React.JSX.Element | null {
  const statusQuery = usePairingStatus(channelId, open);
  const status = statusQuery.data?.status ?? 'STARTING';
  const isPaired = status === 'WORKING';

  // QR só é buscado enquanto NÃO estiver pareado. Pedir QR de sessão ativa
  // derrubaria o pareamento — o backend responde 409, mas nem chegamos lá.
  const qrQuery = usePairingQr(channelId, open && !isPaired);

  // Esc fecha — acessibilidade básica de modal.
  React.useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={`Parear WhatsApp de ${agentName}`}
    >
      {/* Overlay */}
      <button
        type="button"
        aria-label="Fechar"
        onClick={onClose}
        className="absolute inset-0 bg-ink/40 backdrop-blur-sm"
      />

      {/* Card */}
      <div
        className="relative w-full max-w-md rounded-md border border-border bg-surface-1 p-6 flex flex-col gap-5"
        style={{ boxShadow: 'var(--elev-3)' }}
      >
        <div>
          <h2
            className="font-display font-bold text-ink leading-tight"
            style={{ fontSize: 'var(--text-xl)', letterSpacing: '-0.028em' }}
          >
            Conectar WhatsApp
          </h2>
          <p className="font-sans text-sm text-ink-3 mt-1">{agentName}</p>
        </div>

        {/* Selo de status */}
        <div
          className={cn(
            'self-start rounded-sm border px-2.5 py-1',
            'font-sans text-xs font-semibold',
            statusTone(status),
          )}
          aria-live="polite"
        >
          {PAIRING_STATUS_LABEL[status]}
        </div>

        {isPaired ? (
          <div className="flex flex-col gap-3 py-4 text-center">
            <p className="font-sans text-sm text-ink">
              Aparelho conectado. As conversas deste número passam a aparecer no sistema.
            </p>
            {statusQuery.data?.phone_e164 !== null && statusQuery.data?.phone_e164 !== undefined ? (
              <p className="font-mono text-xs text-ink-3">{statusQuery.data.phone_e164}</p>
            ) : null}
          </div>
        ) : (
          <>
            <ol className="font-sans text-sm text-ink-2 flex flex-col gap-1.5 list-decimal list-inside">
              <li>Abra o WhatsApp no celular do vendedor</li>
              <li>
                Toque em <strong className="font-semibold text-ink">Aparelhos conectados</strong>
              </li>
              <li>Escaneie o código abaixo</li>
            </ol>

            <div className="flex items-center justify-center min-h-[240px] rounded-sm border border-border-strong bg-surface-2 p-3">
              {qrQuery.isLoading ? (
                <div
                  className="w-6 h-6 rounded-full border-2 border-border-strong border-t-azul animate-spin"
                  role="status"
                  aria-label="Gerando código…"
                />
              ) : qrQuery.isError ? (
                <p className="font-sans text-sm text-danger text-center px-4">
                  Não foi possível gerar o código. Verifique se o serviço de WhatsApp está no ar.
                </p>
              ) : qrQuery.data !== undefined ? (
                <img
                  src={`data:${qrQuery.data.mime_type};base64,${qrQuery.data.qr_base64}`}
                  alt="QR code para parear o WhatsApp"
                  className="w-[220px] h-[220px] rounded-sm"
                />
              ) : null}
            </div>

            <p className="font-sans text-xs text-ink-3">
              O código expira em cerca de 1 minuto. Se expirar, gere outro.
            </p>
          </>
        )}

        <div className="flex items-center justify-between gap-3 pt-1">
          {!isPaired ? (
            <button
              type="button"
              onClick={() => void qrQuery.refetch()}
              disabled={qrQuery.isFetching}
              className={cn(
                'font-sans text-sm font-medium px-3 py-2 rounded-sm',
                'border border-border-strong text-ink',
                'transition-colors duration-fast hover:bg-surface-hover',
                'disabled:opacity-50 disabled:cursor-not-allowed',
              )}
            >
              Gerar novo código
            </button>
          ) : (
            <span />
          )}

          <button
            type="button"
            onClick={onClose}
            className={cn(
              'font-sans text-sm font-semibold px-4 py-2 rounded-sm',
              'bg-azul text-white',
              'transition-[background,transform] duration-fast hover:brightness-110',
            )}
          >
            {isPaired ? 'Concluir' : 'Fechar'}
          </button>
        </div>
      </div>
    </div>
  );
}
